import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, RequestError, Service } from 'webapp-types';
import { BillingCycle, BillingFeature } from 'webapp/enums/enums';
import { getFormattedMonthlyPrice, getFormattedPrice } from 'webapp/utils/number';

import BarLoaderComponent from 'webapp/components/bar-loader';
import BillingPlanTopFeaturesComponent from 'webapp/components/billing/plan-top-features';
import BillingPopupPlanUpgradeBaseComponent from 'webapp/components/billing/popup-plan-upgrade-base';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import type Promotion from 'webapp/models-custom/promotion';

import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

type SubscriptionPreview = APIData.Billing.SubscriptionPreview;

export default class BillingPopupPlanUpgradeFeatureComponent extends BillingPopupPlanUpgradeBaseComponent<PopupData.Billing.PlanUpgradeForFeature> {
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;

	@tracked subcriptionPreview?: SubscriptionPreview;
	@tracked requestInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const feature = this.feature;

		if (feature) {
			const eventProperties: {
				feature: BillingFeature;
				trackingOrigin?: string;
			} = {
				feature,
			};

			const trackingOrigin = this.trackingOrigin,
				useOriginForTrackingFeature = this.popupData.useOriginForTrackingFeature;
			if (useOriginForTrackingFeature && trackingOrigin) {
				eventProperties.trackingOrigin = trackingOrigin;
			}

			this.billingService.trackLimitationPopupEvent(eventProperties);
		}

		const targetPlan = this.targetPlan;
		if (targetPlan && !targetPlan.isCustom) {
			this.requestInProgress = true;

			this.billingService
				.requestSubscriptionPreview(targetPlan.productId, [], true)
				.then((reply) => {
					this.subcriptionPreview = reply;
				})
				.catch((errorData: RequestError) => {
					this.requestService.handleGenericThrownError(errorData);
				})
				.finally(() => {
					// Set timeout to prevent cached value to cause dialog to close prematurely
					setTimeout(() => {
						this.requestInProgress = false;
					}, 100);
				});
		}
	}

	get feature() {
		return this.popupData.feature;
	}

	get onPlanUpgradeFn() {
		return this.popupData.onPlanUpgrade;
	}

	get trackingOrigin() {
		return this.popupData.trackingOrigin;
	}

	get mainFeature() {
		return this.feature === BillingFeature.AI_DATABOARDS
			? BillingFeature.GENERATIVE_AI
			: this.feature;
	}

	get isFeatureCountable() {
		const feature = this.feature;
		return feature && this.targetPlan?.features.isCountable(feature);
	}

	get targetPlan(): BillingPlan | null {
		const feature = this.mainFeature;
		if (feature) {
			return this.billingService.getNextPlanForFeatureWithSameBillingCycle(feature, true);
		}
		return null;
	}

	get upgradeText() {
		if (!this.planName) {
			return htmlSafe('Upgrade your plan to unlock this feature');
		}

		let text = `${this.isCanceledSubscription ? 'Renew' : 'Upgrade to'} <span>${this.planName}</span>`;
		if (this.isFeatureCountable) {
			const count = this.targetPlan?.features.getValue(this.feature) || 'unlimited';
			text += ` for ${count} ${this.featureName}`;
		} else {
			text += ' to unlock this feature';
		}
		return htmlSafe(text);
	}

	get featureName() {
		const feature = this.feature;
		return (feature && this.billingService.getFeatureName(feature)) || feature || null;
	}

	get availableTrialPromotion(): Promotion | undefined {
		const planForFeature = this.billingService.getPlanForTrial(this.mainFeature);
		return planForFeature
			? this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
			: undefined;
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial && !this.targetPlan?.isCustom;
	}

	get subcriptionPreviewPricesForCycle() {
		const plan = this.billingService.activePlan;
		if (!plan) {
			return;
		}
		const billingCycle = plan.billingCycle || BillingCycle.MONTHLY;
		return this.subcriptionPreview?.prices[billingCycle];
	}

	get planPrice() {
		const subcriptionPreviewPricesForCycle = this.subcriptionPreviewPricesForCycle;
		if (!subcriptionPreviewPricesForCycle) {
			return null;
		}

		return getFormattedPrice(
			subcriptionPreviewPricesForCycle.currentPlanPriceDiffPerMonth,
			0,
			true,
		);
	}

	get planPriceAnnually() {
		const subcriptionPreviewPricesForCycle = this.subcriptionPreviewPricesForCycle;
		if (!subcriptionPreviewPricesForCycle) {
			return;
		}

		return getFormattedPrice(subcriptionPreviewPricesForCycle.currentPlanPriceDiff, 0);
	}

	get fullPlanPriceMonthly() {
		const targetPlan = this.targetPlan;
		if (!targetPlan) {
			return '';
		}
		return getFormattedMonthlyPrice(targetPlan.price, 0, targetPlan.monthsInCycle);
	}

	get cycleText() {
		if (this.billingService.activePlan?.isYearly) {
			return 'annually';
		} else {
			return 'quarterly';
		}
	}

	get pricingTitle(): string | null {
		return 'Upgrade for an additional';
	}

	get isCanceledSubscription() {
		return this.billingService.isCanceled;
	}

	get upgradeButtonText() {
		if (this.targetPlan?.isCustom) {
			return 'Talk to sales';
		} else {
			return this.isCanceledSubscription ? 'Add card' : 'Upgrade now';
		}
	}

	@action
	openPlansPage() {
		if (this.popupData.onNavigateToClick) {
			this.popupData.onNavigateToClick();
		} else {
			this.navigationService.transitionToRoute('settings.billing.plans');
		}
	}

	@action
	override openSubscriptionPopup() {
		super.openSubscriptionPopup(
			this.trackingOrigin as TrackingOriginForSubscriptionChangePopup,
		);
	}

	@action
	handleUpgradeClick() {
		if (this.targetPlan?.isCustom) {
			window.open(this.billingService.customPlanMeetingUrl, '_blank');
		} else if (this.isCanceledSubscription) {
			this.billingService.changePayment();
		} else {
			this.openSubscriptionPopup();
		}
	}
	<template>
		<div class="content content--split content--{{this.planType}}">
			<div class="content__left {{unless this.planHightlightText 'content__left--no-text'}}">
				<div class="content__image-container">
					<img src={{this.imageSrc}} alt="Features" />
				</div>
				<div class="content__text-container">
					<div class="content__text content__text--highlight">
						{{{this.planHightlightText}}}
					</div>
				</div>
			</div>
			<div class="content__right content__right--{{this.targetPlan.planType}}">
				<div class="content__text content__text--upgrade">{{this.upgradeText}}</div>

				{{#if this.targetPlan}}
					<BillingPlanTopFeaturesComponent @model={{this.targetPlan}} />
				{{/if}}

				<div class="content__features">
					{{#each this.planFeatures as |feature|}}
						<div class="content__feature-item">
							<CommonUiIconComponent @name="Check" @size="md" />
							{{feature}}
						</div>
					{{/each}}
				</div>
				{{#if this.requestInProgress}}
					<div class="loader__wrapper">
						<BarLoaderComponent />
					</div>
				{{else}}
					{{#if this.planPrice}}
						<div class="content__pricing-title">{{this.pricingTitle}}</div>
						<div class="content__pricing">
							<span class="content__pricing-symbol">$</span>
							<span class="content__pricing-amount">{{this.planPrice}}</span>
							<span class="content__pricing-period">/month</span>
							{{#unless this.isCurrentCycleMonthly}}
								<div class="content__pricing-additional">
									or additional
									{{this.planPriceAnnually}}
									billed
									{{this.cycleText}}
								</div>
							{{/unless}}
						</div>
						<div class="content__pricing-title content__pricing-title--secondary">
							Full plan price:
							<strong>
								{{this.fullPlanPriceMonthly}}/month
							</strong>
						</div>
					{{else if this.targetPlan.isCustom}}
						<p>
							Custom is quoted by our sales team based on data volume and services
							needed.
						</p>
					{{/if}}
				{{/if}}
				<div class="content__button-container">
					{{#if this.canRequestTrial}}
						<CommonRequestTrialButtonComponent
							@feature={{this.mainFeature}}
							@trackingOrigin={{this.trackingOrigin}}
							@useTargetPlan={{true}}
						/>
					{{else}}
						<SubmitButtonComponent
							@text={{this.upgradeButtonText}}
							@noDelay={{true}}
							@onTrigger={{this.handleUpgradeClick}}
						/>
					{{/if}}
					<SubmitButtonComponent
						@text="Compare plans"
						@theme="blue-outlined"
						@onTrigger={{this.openPlansPage}}
					/>
				</div>
			</div>
		</div>
	</template>
}
